/**
 * Analytics tracker - captures page views and affiliate clicks
 * Sends events to POST /api/track
 */
import { APR_PRODUCTS } from '../data/aprProducts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function sendEvent(data) {
  fetch(`${API_URL}/api/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).catch(() => {}); // fire-and-forget
}

export function trackPageView(url) {
  sendEvent({
    event_type: 'page_view',
    url,
    referrer: document.referrer || '',
  });
  // Also fire Google Analytics 4 page_view — gtag was loaded in index.html with
  // send_page_view:false so we own all SPA route changes here.
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.origin + url,
      page_title: document.title,
    });
  }
}

// --- GA4 custom event helpers (CRO tracking) ---
export function trackGAEvent(name, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

// --- GTM dataLayer helpers (container GTM-TQJJKCBB in index.html) ---
export function pushToDataLayer(event, params = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
  } catch { /* analytics must never break the app */ }
}

// Partner/affiliate events: GTM dataLayer push (container triggers) AND direct
// GA4 gtag dispatch (G-B0F5VTHC2L /g/collect) so receipt never depends on
// unverified GTM container tag configuration.
export function trackPartnerEvent(event, params = {}) {
  pushToDataLayer(event, params);
  trackGAEvent(event, params);
}

// GA4-ecommerce-shaped push; clears the previous ecommerce object first (GTM best practice).
function pushEcommerce(event, ecommerce, extra = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce, ...extra });
  } catch {}
}

export function trackGenerateLead(source, magnetId = '') {
  trackGAEvent('generate_lead', { lead_source: source, magnet_id: magnetId, currency: 'USD', value: 0 });
  pushToDataLayer('lead_magnet_signup', { lead_source: source, magnet_id: magnetId });
}

export function trackLogin(method) {
  trackGAEvent('login', { method });
}

export function trackSignUp(method) {
  trackGAEvent('sign_up', { method });
}

export function trackBoostArticle(slug, keyword) {
  trackGAEvent('boost_article', { article_slug: slug, keyword });
}

export function trackAddToCart(item) {
  const items = [{
    item_id: item.product_id,
    item_name: item.name,
    price: item.price,
    quantity: item.quantity,
  }];
  const value = (item.price || 0) * (item.quantity || 1);
  trackGAEvent('add_to_cart', { currency: 'USD', value, items });
  pushEcommerce('add_to_cart', { currency: 'USD', value, items });
}

export function trackViewItem(product) {
  if (!product) return;
  const items = [{
    item_id: product.id,
    item_name: product.title || product.name,
    item_category: product.category || '',
    price: product.price,
    quantity: 1,
  }];
  trackGAEvent('view_item', { currency: 'USD', value: product.price || 0, items });
  pushEcommerce('view_item', { currency: 'USD', value: product.price || 0, items });
}

// Fired right before any redirect to Shopify checkout (Buy Now, cart, City Series).
export function trackBeginCheckout(value, items, source = '') {
  trackGAEvent('begin_checkout', { currency: 'USD', value, items });
  pushEcommerce('begin_checkout', { currency: 'USD', value, items }, { checkout_source: source });
}

export function trackPurchase(order, transactionId) {
  const dedupKey = `ga4_purchase_${transactionId}`;
  try {
    if (localStorage.getItem(dedupKey)) return;
    localStorage.setItem(dedupKey, '1');
  } catch {}
  const items = (order?.items || []).map((it) => ({
    item_name: it.name,
    price: it.price,
    quantity: it.quantity,
  }));
  trackGAEvent('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value: order?.total || 0,
    shipping: order?.shipping_cost || 0,
    items,
  });
  pushEcommerce('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value: order?.total || 0,
    shipping: order?.shipping_cost || 0,
    items,
  });
}

export function trackAffiliateClick(url, utmParams = {}, productName = '', sourcePage = '', merchant = '') {
  sendEvent({
    event_type: 'affiliate_click',
    url,
    product_name: productName,
    merchant: merchant || merchantFromUrl(url),
    source_page: sourcePage || window.location.pathname,
    utm_source: utmParams.utm_source || '',
    utm_medium: utmParams.utm_medium || '',
    utm_campaign: utmParams.utm_campaign || '',
    utm_content: utmParams.utm_content || '',
    referrer: window.location.pathname,
  });
}

// Identify the merchant/partner from an affiliate URL.
function merchantFromUrl(url = '') {
  const u = url.toLowerCase();
  if (u.includes('americanpeptideresearch')) return 'apr';
  if (u.includes('pepticool')) return 'pepticool';
  if (u.includes('peptiq.io')) return 'peptiq';
  if (u.includes('echowater')) return 'echo';
  if (u.includes('biminihydrotherapy') || u.includes('biminipureo2')) return 'bimini';
  if (u.includes('amazon.')) return 'amazon';
  if (u.includes('shopify.com')) return 'shopify';
  return 'other';
}

// Derive the APR product slug from a canonical /product/{slug}/ref/16/ URL.
function aprSlugFromUrl(url = '') {
  const m = url.match(/\/product\/([^/]+)\//);
  return m ? m[1] : '';
}

/**
 * Intercepts clicks on affiliate links (links with utm_ params or external links to partners)
 */
export function initAffiliateClickTracker() {
  if (window.__aprClickTrackerInstalled) return;
  window.__aprClickTrackerInstalled = true;
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href) return;

    // Track external links with UTM params
    if (href.includes('utm_') || href.includes('rfsn=') || href.includes('?ref=')) {
      const placementEl = link.closest('[data-placement-id]');
      const placementTag = placementEl ? `placement:${placementEl.getAttribute('data-placement-id')}` : '';
      try {
        const url = new URL(href, window.location.origin);
        trackAffiliateClick(href, {
          utm_source: url.searchParams.get('utm_source') || '',
          utm_medium: url.searchParams.get('utm_medium') || '',
          utm_campaign: url.searchParams.get('utm_campaign') || '',
          utm_content: url.searchParams.get('utm_content') || placementTag,
        }, '', window.location.pathname, merchantFromUrl(href));
      } catch {
        trackAffiliateClick(href);
      }
    }
    // Track links to known partner domains
    else if (
      href.includes('tidd.ly') ||
      href.includes('centrtrained.com') ||
      href.includes('bfrpsn.com') ||
      href.includes('bfrpsn.net') ||
      href.includes('bfrpay.com') ||
      href.includes('biminihydrotherapy.com') ||
      href.includes('shopify.com') ||
      href.includes('echowater.com') ||
      href.includes('amazon.com') ||
      href.includes('americanpeptideresearch.net') ||
      href.includes('peptiq.io')
    ) {
      const merchant = merchantFromUrl(href);
      let productName = '';
      if (merchant === 'apr') {
        // Prefer an explicit data attribute, then the product slug in the URL,
        // then a nearby heading — so every APR shop card/CTA attributes correctly.
        const holder = link.closest('[data-apr-product]') || (link.hasAttribute('data-apr-product') ? link : null);
        const slug = (holder && holder.getAttribute('data-apr-product')) || aprSlugFromUrl(href);
        productName =
          (holder && holder.getAttribute('data-apr-name')) ||
          (slug && APR_PRODUCTS[slug] && APR_PRODUCTS[slug].name) ||
          slug ||
          link.textContent?.trim() ||
          'APR Store';
      } else {
        const card = link.closest('[data-testid]');
        productName = card?.querySelector('h4')?.textContent || link.textContent?.trim() || '';
      }
      trackAffiliateClick(href, {}, productName, window.location.pathname, merchant);
    }
  });
}
